/**
 * MyStatsPage Component
 * Shows comprehensive stats about the user's watched movies
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getWatchedMoviesList,
  getWatchedStats,
  type GetWatchedMoviesParams,
  type WatchedMovieItem,
  type WatchedMoviesListResponse,
  type WatchedStats,
} from '../api';
import {
  formatRuntime,
  HorizontalBar,
  RankedList,
  StatCard,
  StatsSection,
} from './StatsComponents';

// ISO 639-1 language code to display name mapping
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  hi: 'Hindi',
  ar: 'Arabic',
  ru: 'Russian',
  nl: 'Dutch',
  sv: 'Swedish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  pl: 'Polish',
  tr: 'Turkish',
  th: 'Thai',
  id: 'Indonesian',
  ms: 'Malay',
  vi: 'Vietnamese',
  tl: 'Filipino',
  cs: 'Czech',
  el: 'Greek',
  he: 'Hebrew',
  hu: 'Hungarian',
  ro: 'Romanian',
  uk: 'Ukrainian',
  bn: 'Bengali',
  ta: 'Tamil',
  te: 'Telugu',
  ml: 'Malayalam',
  cn: 'Cantonese',
  fa: 'Persian',
  la: 'Latin',
};

function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code] || code.toUpperCase();
}

// ==================== Sub-Components ====================

function MovieGrid({ movies, loading }: { movies: WatchedMovieItem[]; loading: boolean }) {
  if (loading && movies.length === 0) {
    return <div className="empty">Loading movies...</div>;
  }

  if (movies.length === 0) {
    return <div className="empty">No movies match your filters</div>;
  }

  return (
    <div className="stats-movie-grid">
      {movies.map((movie) => (
        <Link key={movie.tmdb_id} to={`/movies/${movie.tmdb_id}`} className="stats-movie-card">
          {movie.poster_url ? (
            <img src={movie.poster_url} alt={movie.title || ''} loading="lazy" />
          ) : (
            <div className="stats-movie-no-poster">{movie.title || '?'}</div>
          )}
          <div className="stats-movie-info">
            <div className="stats-movie-title">{movie.original_title || movie.title}</div>
            <div className="stats-movie-meta">
              {movie.year && <span>{movie.year}</span>}
              {movie.runtime && <span>{movie.runtime}m</span>}
              {movie.vote_average ? <span>{movie.vote_average.toFixed(1)}</span> : null}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ==================== Main Component ====================

export default function MyStatsPage() {
  const [stats, setStats] = useState<WatchedStats | null>(null);
  const [moviesList, setMoviesList] = useState<WatchedMoviesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [moviesLoading, setMoviesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Browse filters
  const [browsePage, setBrowsePage] = useState(1);
  const [browseSort, setBrowseSort] = useState<GetWatchedMoviesParams['sort']>('watched_at');
  const [browseOrder, setBrowseOrder] = useState<'asc' | 'desc'>('desc');
  const [browseGenre, setBrowseGenre] = useState<string>('');
  const [browseDecade, setBrowseDecade] = useState<string>('');
  const [browseLanguage, setBrowseLanguage] = useState<string>('');

  // Load stats on mount
  useEffect(() => {
    let cancelled = false;

    async function loadStats() {
      setLoading(true);
      setError(null);
      try {
        const data = await getWatchedStats();
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadStats();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load browse movies when filters change
  const loadMovies = useCallback(async () => {
    setMoviesLoading(true);
    try {
      const data = await getWatchedMoviesList({
        page: browsePage,
        limit: 20,
        sort: browseSort,
        order: browseOrder,
        genre: browseGenre || undefined,
        decade: browseDecade || undefined,
        language: browseLanguage || undefined,
      });
      setMoviesList(data);
    } catch (err) {
      console.error('Failed to load movies list:', err);
    } finally {
      setMoviesLoading(false);
    }
  }, [browsePage, browseSort, browseOrder, browseGenre, browseDecade, browseLanguage]);

  useEffect(() => {
    loadMovies();
  }, [loadMovies]);

  // Reset page when filters change
  useEffect(() => {
    setBrowsePage(1);
  }, [browseSort, browseOrder, browseGenre, browseDecade, browseLanguage]);

  if (loading) {
    return (
      <div className="stats-loading">
        <div className="stats-loading-spinner" />
        <p>Crunching your movie stats...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '32px' }}>
        <p style={{ color: '#f87171' }}>{error}</p>
        <button onClick={() => window.location.reload()} style={{ marginTop: '12px' }}>
          Try Again
        </button>
      </div>
    );
  }

  if (!stats || stats.total_movies === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
        <h2 style={{ marginBottom: '12px' }}>No Movies Watched Yet</h2>
        <p style={{ opacity: 0.7, marginBottom: '20px' }}>
          Start marking movies as watched to see your stats here!
        </p>
        <Link to="/explore" className="link-button" style={{ display: 'inline-flex' }}>
          Explore Movies
        </Link>
      </div>
    );
  }

  const maxGenreCount = stats.genres.length > 0 ? stats.genres[0].count : 1;
  const maxDecadeCount =
    stats.decades.length > 0 ? Math.max(...stats.decades.map((d) => d.count)) : 1;
  const maxRatingCount =
    stats.ratings.distribution.length > 0
      ? Math.max(...stats.ratings.distribution.map((r) => r.count))
      : 1;
  const maxLanguageCount = stats.languages.length > 0 ? stats.languages[0].count : 1;

  return (
    <div className="stats-page">
      <h2 className="stats-page-title">My Stats</h2>

      {/* Hero stat cards */}
      <div className="stats-hero">
        <StatCard label="Watched" value={stats.total_movies} sub="movies" />
        <StatCard
          label="Watch Time"
          value={formatRuntime(stats.total_runtime_minutes)}
          sub={`${Math.round(stats.total_runtime_minutes / 60)} hrs`}
        />
        {stats.ratings.average_imdb && (
          <StatCard label="Avg. Rating" value={stats.ratings.average_imdb.toFixed(1)} sub="IMDb" />
        )}
      </div>

      {/* Oldest/newest trivia */}
      {(stats.oldest_movie || stats.newest_movie) && (
        <div className="stats-trivia">
          {stats.oldest_movie && (
            <div className="stats-trivia-item">
              <span className="stats-trivia-label">Oldest</span>
              <span>
                {stats.oldest_movie.title} ({stats.oldest_movie.year})
              </span>
            </div>
          )}
          {stats.newest_movie && (
            <div className="stats-trivia-item">
              <span className="stats-trivia-label">Newest</span>
              <span>
                {stats.newest_movie.title} ({stats.newest_movie.year})
              </span>
            </div>
          )}
        </div>
      )}

      {/* Genre breakdown */}
      <StatsSection title="Genres">
        <HorizontalBar items={stats.genres} maxCount={maxGenreCount} />
      </StatsSection>

      {/* Decades distribution */}
      <StatsSection title="Decades">
        <HorizontalBar
          items={stats.decades.map((d) => ({ name: d.name, count: d.count }))}
          maxCount={maxDecadeCount}
        />
      </StatsSection>

      {/* Top Directors */}
      <StatsSection title="Top Directors">
        <RankedList items={stats.top_directors} emptyMessage="No director data available" />
      </StatsSection>

      {/* Top Actors */}
      <StatsSection title="Top Actors">
        <RankedList items={stats.top_actors} emptyMessage="No actor data available" />
      </StatsSection>

      {/* Rating distribution */}
      <StatsSection title="Rating Distribution">
        <HorizontalBar
          items={stats.ratings.distribution.map((r) => ({ name: r.name, count: r.count }))}
          maxCount={maxRatingCount}
        />
      </StatsSection>

      {/* Language breakdown */}
      <StatsSection title="Languages">
        <HorizontalBar
          items={stats.languages.map((l) => ({ name: getLanguageName(l.name), count: l.count }))}
          maxCount={maxLanguageCount}
        />
      </StatsSection>

      {/* Browse all watched movies */}
      <StatsSection title="All Watched Movies">
        <div className="stats-browse-controls">
          {/* Sort row */}
          <div className="stats-browse-sort-row">
            <select
              value={browseSort}
              onChange={(e) => setBrowseSort(e.target.value as GetWatchedMoviesParams['sort'])}
              className="stats-sort-select"
            >
              <option value="watched_at">Date Watched</option>
              <option value="title">Title</option>
              <option value="year">Year</option>
              <option value="rating">Rating</option>
            </select>

            <button
              className="secondary stats-sort-btn"
              onClick={() => setBrowseOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
              title={browseOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {browseOrder === 'asc' ? (
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 5v14M19 12l-7 7-7-7" />
                </svg>
              )}
            </button>

            {moviesList && (
              <div className="stats-browse-count">
                {moviesList.pagination.total}{' '}
                {moviesList.pagination.total === 1 ? 'movie' : 'movies'}
              </div>
            )}
          </div>

          {/* Filter row */}
          {moviesList?.available_filters && (
            <div className="stats-browse-filter-row">
              <select value={browseGenre} onChange={(e) => setBrowseGenre(e.target.value)}>
                <option value="">All Genres</option>
                {moviesList.available_filters.genres.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>

              <select value={browseDecade} onChange={(e) => setBrowseDecade(e.target.value)}>
                <option value="">All Decades</option>
                {moviesList.available_filters.decades.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>

              <select value={browseLanguage} onChange={(e) => setBrowseLanguage(e.target.value)}>
                <option value="">All Languages</option>
                {moviesList.available_filters.languages.map((l) => (
                  <option key={l} value={l}>
                    {getLanguageName(l)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <MovieGrid movies={moviesList?.movies || []} loading={moviesLoading} />

        {/* Pagination */}
        {moviesList && moviesList.pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={browsePage <= 1}
              onClick={() => setBrowsePage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {moviesList.pagination.page} of {moviesList.pagination.totalPages}
            </span>
            <button
              disabled={browsePage >= moviesList.pagination.totalPages}
              onClick={() => setBrowsePage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </StatsSection>
    </div>
  );
}
